import React, { FC, useState } from "react";

import dayjs from "dayjs";

import {
  FixtureItem,
  TipType,
  useGetFixtures,
  useGetTipsByFixture,
} from "@api";

import { copyToClipboard } from "@utils";

import { CalendarSlider, TabItem, TabList, Tabs } from "@ui/components";
import { Button, Icon, Text } from "@ui/elements";
import { Control, Field, Input } from "@ui/forms";
import { Column, Columns, Stack } from "@ui/layout";

import "./FixtureList.scss";
import {
  MatchDetailsDrawer,
  TipBox,
  TipsTable,
  TipTableSkeleton,
  toaster,
} from "@components";

type TipsViewerProps = {
  fixture: FixtureItem;
};

const TipsViewer: FC<TipsViewerProps> = ({ fixture }) => {
  const [selectedTab, setSelectedTab] = useState<TipType>("resultOriented");
  const [matchAnalysisOpen, setMatchAnalysisOpen] = useState(false);
  const { data: tipsData, isLoading: tipsLoading } = useGetTipsByFixture(
    String(fixture.fixture.id),
    {
      type: selectedTab,
    },
  );

  const handleIdCopy = async () => {
    await copyToClipboard(fixture.fixture.id);
    toaster.success({
      title: "Fixture ID copied to clipboard",
    });
  };

  return (
    <TipBox fixture={fixture} hasTips={!!tipsData?.length}>
      <Stack direction="column">
        <MatchDetailsDrawer
          isOpen={matchAnalysisOpen}
          onClose={() => setMatchAnalysisOpen(false)}
          fixture={fixture}
        />
        <Stack>
          <Button onClick={() => setMatchAnalysisOpen(true)}>
            View Analysis
          </Button>
          <Field hasAddons onClick={handleIdCopy}>
            <Control>
              <Input value={fixture.fixture.id} disabled />
            </Control>
            <Control>
              <Button>
                <Icon icon="Copy" />
              </Button>
            </Control>
          </Field>
        </Stack>

        <Tabs
          active={selectedTab}
          onChange={(tab) => setSelectedTab(tab as TipType)}
        >
          <TabList>
            <TabItem tab="resultOriented">
              <Icon icon="Target" /> Result Oriented
            </TabItem>
            <TabItem tab="value">
              <Icon icon="ChartBar" /> Value Based
            </TabItem>
            <TabItem tab="value-v2">
              <Icon icon="Coins" /> Upset Model
            </TabItem>
          </TabList>
          {!tipsLoading && tipsData && tipsData.length === 0 && (
            <Text>
              No tips found for this fixture. Please check back later.
            </Text>
          )}
          {tipsLoading && <TipTableSkeleton />}
          {!tipsLoading && tipsData && tipsData.length > 0 && (
            <TipsTable
              tips={tipsData}
              fixture={fixture}
              type={selectedTab}
              allowCurrency
              allowSaveEdit
            />
          )}
        </Tabs>
      </Stack>
    </TipBox>
  );
};

export const FixtureList = () => {
  const [date, setDate] = useState(dayjs().format("YYYY-MM-DD"));
  const { data: fixturesData, isLoading } = useGetFixtures({
    from: date,
    to: date,
  });

  return (
    <Stack direction="column">
      <Text variant="secondary">Fixtures</Text>
      <CalendarSlider value={date} onChange={setDate} />
      <Columns>
        <Column>
          <Stack direction="column" gap="xl">
            {isLoading && <Text>Loading ...</Text>}
            {fixturesData?.map((fixture) => (
              <TipsViewer
                fixture={fixture}
                key={`admin-tip-card-${fixture.fixture.id}`}
              />
            ))}
          </Stack>
        </Column>
      </Columns>
    </Stack>
  );
};
